import styled from "styled-components";
import {useState, useEffect, useRef, useCallback} from 'react';
import {FaSearch} from 'react-icons/fa';
import {useNavigate, useLocation} from 'react-router-dom'
import cacheManager from '../utils/cacheManager';

function Search() {

  const [input, setInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Reset search state when location changes
  useEffect(() => {
    setIsSearching(false);
    // Only clear input if we're navigating away from search results
    if (!location.pathname.startsWith('/searched/')) {
      setInput("");
    }
  }, [location.pathname]);

  // Listen for search completion event from Searched component
  useEffect(() => {
    const handleSearchComplete = () => {
      setIsSearching(false);
    };
    window.addEventListener('search-complete', handleSearchComplete);
    return () => window.removeEventListener('search-complete', handleSearchComplete);
  }, []);

  // Fallback: Reset searching state after timeout (safety net)
  useEffect(() => {
    if (isSearching) {
      const fallbackTimer = setTimeout(() => {
        setIsSearching(false);
      }, 2000); // 2 second fallback
      return () => clearTimeout(fallbackTimer);
    }
  }, [isSearching]);

  // Click outside handler to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch autocomplete suggestions
  const fetchSuggestions = useCallback(async (query) => {
    // Don't fetch if submitting or invalid query
    if (isSubmittingRef.current || !query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Check cache first
    const cacheKey = `autocomplete-${query.toLowerCase()}`;
    const cached = await cacheManager.get(cacheKey, 'SEARCH');
    if (cached) {
      // Only update if not submitting
      if (!isSubmittingRef.current) {
        setSuggestions(cached);
      }
      return;
    }

    try {
      const response = await fetch(
        `https://api.spoonacular.com/recipes/autocomplete?apiKey=${process.env.REACT_APP_API_KEY}&query=${encodeURIComponent(query)}&number=8`,
        { signal: abortControllerRef.current.signal }
      );
      const data = await response.json();

      // Only update state if not submitting
      if (!isSubmittingRef.current) {
        setSuggestions(data || []);
      }
      // Cache for 1 day
      await cacheManager.set(cacheKey, data || [], 'SEARCH');
    } catch (error) {
      if (error.name === 'AbortError') {
        // Fetch was cancelled, ignore
        return;
      }
      console.error('Autocomplete error:', error);
      if (!isSubmittingRef.current) {
        setSuggestions([]);
      }
    }
  }, []);

  // Debounced input handler
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    setSelectedIndex(-1);

    // Don't show suggestions if we're in submitting state
    if (isSubmittingRef.current) {
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length >= 2) {
      setShowSuggestions(true);
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const selectSuggestion = (suggestion) => {
    // Mark as submitting
    isSubmittingRef.current = true;

    // Cancel any pending fetches
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setInput(suggestion.title);
    setShowSuggestions(false);
    setSuggestions([]);
    setIsSearching(true);
    navigate("/searched/" + encodeURIComponent(suggestion.title) + "?t=" + Date.now());

    setTimeout(() => {
      isSubmittingRef.current = false;
    }, 100);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      // Let form submission handle Enter
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        selectSuggestion(suggestions[selectedIndex]);
      } else if (input.trim()) {
        // Trigger form submit programmatically
        const form = wrapperRef.current;
        if (form) {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    if (input.trim()) {
      const searchTerm = input.trim();
      const newPath = "/searched/" + encodeURIComponent(searchTerm);

      // Mark as submitting to prevent any async updates
      isSubmittingRef.current = true;

      // Cancel any pending debounced fetch
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      // Abort any in-flight fetch request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Close suggestions immediately and clear state
      setShowSuggestions(false);
      setSuggestions([]);
      setSelectedIndex(-1);
      setIsSearching(true);

      // Blur the input to prevent focus-related suggestion reopening
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Navigate - always use unique timestamp to force re-fetch
      const targetPath = newPath + "?t=" + Date.now();
      navigate(targetPath, { replace: location.pathname.startsWith('/searched/') });

      // Reset submitting flag after navigation
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 100);
    }
  }

  return (
    <FormStyle onSubmit={submitHandler} ref={wrapperRef}>
      <SearchWrapper>
        <SearchIcon isSearching={isSearching}>
          <FaSearch />
        </SearchIcon>
        <SearchInput
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => !isSubmittingRef.current && suggestions.length > 0 && setShowSuggestions(true)}
          type="text"
          value={input}
          placeholder="Search for recipes..."
          isSearching={isSearching}
          autoComplete="off"
        />
        <SearchButton type="submit" disabled={!input.trim() || isSearching}>
          {isSearching ? (
            <SearchSpinner />
          ) : (
            <ArrowIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </ArrowIcon>
          )}
        </SearchButton>
        {showSuggestions && suggestions.length > 0 && !isSearching && (
          <SuggestionsDropdown>
            {suggestions.map((suggestion, index) => (
              <SuggestionItem
                key={suggestion.id}
                isSelected={index === selectedIndex}
                onClick={() => selectSuggestion(suggestion)}
              >
                <SuggestionIcon>🍽️</SuggestionIcon>
                <SuggestionText>{suggestion.title}</SuggestionText>
              </SuggestionItem>
            ))}
          </SuggestionsDropdown>
        )}
      </SearchWrapper>
    </FormStyle>
  )
}

const FormStyle = styled.form`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
`

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`

const SearchIcon = styled.div`
  position: absolute;
  left: var(--spacing-md);
  color: ${props => props.isSearching ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
  transition: color 0.2s ease;

  svg {
    font-size: 1.0625rem;
    animation: ${props => props.isSearching ? 'pulse 1.5s ease-in-out infinite' : 'none'};
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`

const SearchInput = styled.input`
  flex: 1;
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) 2.75rem;
  font-size: 1.0625rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-text-primary);
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg) 0 0 var(--radius-lg);
  border-right: none;
  outline: none;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  height: 52px;
  letter-spacing: -0.01em;

  &::placeholder {
    color: var(--color-text-tertiary);
    font-weight: var(--font-weight-normal);
  }

  &:focus {
    border-color: var(--color-text-secondary);
    background: var(--color-surface-elevated);
  }

  &:hover:not(:focus) {
    border-color: var(--color-border);
  }

  @media screen and (max-width: 900px) {
    font-size: 1rem;
    height: 52px;
    padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) 2.5rem;
  }
`

const SearchButton = styled.button`
  width: 52px;
  height: 52px;
  background: var(--color-text-primary);
  color: var(--color-background);
  border: none;
  border-radius: 0 var(--radius-lg) var(--radius-lg) 0;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media screen and (max-width: 900px) {
    width: 52px;
    height: 52px;
  }
`

const ArrowIcon = styled.svg`
  width: 22px;
  height: 22px;
  stroke-width: 2.5;
`

const SearchSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: var(--color-background);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 8px 24px var(--color-shadow);
  z-index: 100;
  max-height: 320px;
  overflow-y: auto;
  animation: slideDown 0.15s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const SuggestionItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  cursor: pointer;
  transition: background var(--transition-fast);
  background: ${props => props.isSelected ? 'var(--color-surface)' : 'transparent'};

  &:hover {
    background: var(--color-surface);
  }

  &:first-child {
    border-radius: var(--radius-md) var(--radius-md) 0 0;
  }

  &:last-child {
    border-radius: 0 0 var(--radius-md) var(--radius-md);
  }
`

const SuggestionIcon = styled.span`
  font-size: 1rem;
  opacity: 0.7;
`

const SuggestionText = styled.span`
  font-size: 0.9375rem;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default Search
